import type {
  NavigationArgs,
  WorkspaceNavigationArgsSchema,
  WorkspaceNavigationDescriptor,
  WorkspaceNavigationRequest,
  WorkspaceNavigationTarget,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import {
  validateNavigationArgs,
  WORKSPACE_VIEW_MAX_NAVIGATION_VISITS,
} from '@streamlain/copilot-realtime-contract';
import type { LiveData } from '@toeverything/infra';
import type { ReactNode } from 'react';

import { AffineCommandRegistry } from '../../../commands/registry/registry';
import {
  type CommandNavigationSearchBudget,
  commandNavigationSearchText,
  matchCommands,
} from '../../quicksearch/impls/commands';
import { isSemanticDomElementVisible } from './semantic-dom-context';
import { WorkspaceNavigationContextBudget } from './workspace-navigation-context-budget';
import type { WorkspaceViewContextPort } from './workspace-view-context';

export interface UiNavigationDefinition {
  capability?: WorkspaceNavigationDescriptor['capability'];
  id: string;
  label: string;
  /** The icon the entry carries where it is shown, so search shows it too. */
  icon?: ReactNode;
  section?: string;
  description?: string;
  /** The doc this entry opens, so search can show it once next to its own hits. */
  docId?: string;
  keywords: readonly string[];
  parentIds: readonly string[];
  argsSchema: WorkspaceNavigationArgsSchema;
  available: () => boolean;
  activate: (args: NavigationArgs) => void | Promise<void>;
  /**
   * A click on the entry of what is on screen already. True if the entry had something to do
   * there, so nothing is opened again.
   */
  reselect?: () => boolean;
  readTarget: () => WorkspaceNavigationTarget | null;
}
export interface UiNavigationBinding {
  root: HTMLElement;
  element: HTMLElement;
  geometryElement: HTMLElement;
  definitionId: string;
  scope: WorkspaceViewScope;
  withClickArgs: (args: NavigationArgs, click: () => void) => void;
}
export interface UiNavigationSource {
  scope: WorkspaceViewScope;
  ownerId: string;
  items$: LiveData<readonly UiNavigationDefinition[]>;
}
export type WorkspaceNavigationContextReadResult =
  | {
      ok: true;
      actions: readonly WorkspaceNavigationDescriptor[];
      truncated: boolean;
    }
  | { ok: false; reason: 'oversized' | 'invalid' };
export interface WorkspaceDiscoveryPort {
  list(scope: WorkspaceViewScope): readonly WorkspaceNavigationDescriptor[];
  search(
    scope: WorkspaceViewScope,
    query: string,
    limit: number
  ): readonly WorkspaceNavigationDescriptor[];
}
export type UiNavigationResolveResult =
  | {
      ok: true;
      definition: UiNavigationDefinition;
      binding: UiNavigationBinding | null;
    }
  | { ok: false; reason: 'blocked' | 'stale' };
export interface UiNavigationCatalogPort extends WorkspaceDiscoveryPort {
  registerSource(source: UiNavigationSource): () => void;
  readContext(scope: WorkspaceViewScope): WorkspaceNavigationContextReadResult;
  describeActions(
    scope: WorkspaceViewScope,
    ids: readonly string[]
  ): readonly WorkspaceNavigationDescriptor[];
  bind(
    scope: WorkspaceViewScope,
    ownerId: string,
    definitionId: string,
    root: HTMLElement,
    element: HTMLElement,
    geometryElement?: HTMLElement,
    withClickArgs?: (args: NavigationArgs, click: () => void) => void
  ): () => void;
  resolve(request: WorkspaceNavigationRequest): UiNavigationResolveResult;
  resolveParent(
    scope: WorkspaceViewScope,
    actionId: string
  ): UiNavigationResolveResult;
}
export const EMPTY_NAVIGATION_ARGS: WorkspaceNavigationArgsSchema = {
  type: 'object',
  additionalProperties: false,
  properties: {},
  required: [],
};
export class NavigationActivationError extends Error {
  constructor(readonly reason: 'blocked' | 'stale') {
    super(reason);
  }
}
export function navigationActionId(
  scope: WorkspaceViewScope,
  ownerId: string,
  definitionId: string
): string {
  return JSON.stringify([
    scope.workspaceId,
    scope.viewId,
    ownerId,
    definitionId,
  ]);
}
const sameScope = (a: WorkspaceViewScope, b: WorkspaceViewScope) =>
  a.workspaceId === b.workspaceId && a.viewId === b.viewId;

/** Definitions and handlers live only in AffineCommandRegistry; these maps own subscriptions and DOM refs. */
export class WorkspaceNavigationCatalog implements UiNavigationCatalogPort {
  private readonly sourceCleanups = new Map<string, () => void>();
  private readonly bindings = new Map<
    string,
    Set<UiNavigationBinding & { acceptsArgs: boolean }>
  >();
  private disposed = false;
  constructor(
    private readonly context: WorkspaceViewContextPort,
    private readonly activeScope: () => WorkspaceViewScope
  ) {}
  registerSource(source: UiNavigationSource): () => void {
    return AffineCommandRegistry.batch(() => this.replaceSource(source));
  }
  private replaceSource(source: UiNavigationSource): () => void {
    if (
      this.disposed ||
      source.scope.workspaceId !== this.activeScope().workspaceId ||
      !source.ownerId.trim()
    )
      throw new NavigationActivationError('blocked');
    const sourceId = JSON.stringify([
      source.scope.workspaceId,
      source.scope.viewId,
      source.ownerId,
    ]);
    this.sourceCleanups.get(sourceId)?.();
    let releases: Array<() => void> = [],
      stopped = false;
    const subscription = source.items$.subscribe(items =>
      AffineCommandRegistry.batch(() => {
        releases.splice(0).forEach(release => release());
        const ids = new Set<string>();
        for (const definition of items) {
          if (!definition.id.trim() || ids.has(definition.id))
            throw new NavigationActivationError('blocked');
          ids.add(definition.id);
          releases.push(
            AffineCommandRegistry.register({
              id: navigationActionId(
                source.scope,
                source.ownerId,
                definition.id
              ),
              category: 'affine:navigation',
              icon: definition.icon ?? null,
              label: { title: definition.label, subTitle: definition.section },
              preconditionStrategy: () =>
                sameScope(source.scope, this.activeScope()) &&
                definition.available(),
              navigation: {
                scope: { ...source.scope },
                ownerId: source.ownerId,
                definition,
              },
              run: () => {
                if (
                  !sameScope(source.scope, this.activeScope()) ||
                  !definition.available()
                )
                  throw new NavigationActivationError('blocked');
                const args = validateNavigationArgs(definition.argsSchema, {});
                if (!args) throw new NavigationActivationError('blocked');
                return definition.activate(args);
              },
            })
          );
        }
        // Navigation belongs to this view's existing Workbench owner. Handler identity
        // changes must invalidate pending intents even when descriptor text is equal.
        this.context.invalidate(source.scope, 'workbench');
      })
    );
    const cleanup = () =>
      AffineCommandRegistry.batch(() => {
        if (stopped) return;
        stopped = true;
        subscription.unsubscribe();
        releases.splice(0).forEach(release => release());
        this.context.invalidate(source.scope, 'workbench');
        if (this.sourceCleanups.get(sourceId) === cleanup)
          this.sourceCleanups.delete(sourceId);
      });
    this.sourceCleanups.set(sourceId, cleanup);
    return cleanup;
  }
  private commands(scope: WorkspaceViewScope) {
    if (this.disposed || !sameScope(scope, this.activeScope())) return [];
    return [...AffineCommandRegistry.iterateNavigationCommands(scope)].filter(
      command =>
        command.navigation &&
        sameScope(command.navigation.scope, scope) &&
        command.navigation.definition.available()
    );
  }
  private descriptor(
    command: ReturnType<typeof AffineCommandRegistry.getAll>[number],
    commands: ReadonlyMap<
      string,
      ReturnType<typeof AffineCommandRegistry.getAll>[number]
    >,
    budget?: CommandNavigationSearchBudget
  ): WorkspaceNavigationDescriptor {
    const navigation = command.navigation!,
      definition = navigation.definition;
    return {
      ...(definition.capability ? { capability: definition.capability } : {}),
      actionId: command.id,
      ownerId: navigation.ownerId,
      kind: 'navigate',
      label: definition.label,
      searchText: commandNavigationSearchText(command, commands, budget),
      argsSchema: definition.argsSchema,
      available: true,
    };
  }
  /**
   * The automatic offer is the search entry alone: the destinations cost every model call their
   * bytes, while one search reaches the complete canonical catalogue when the AI wants to go somewhere.
   */
  readContext(scope: WorkspaceViewScope): WorkspaceNavigationContextReadResult {
    if (this.disposed || !sameScope(scope, this.activeScope()))
      return { ok: true, actions: [], truncated: false };
    const budget = new WorkspaceNavigationContextBudget();
    const commands = new Map<
      string,
      ReturnType<typeof AffineCommandRegistry.getAll>[number]
    >();
    let visits = 0;
    for (const command of AffineCommandRegistry.iterateNavigationCommands(
      scope,
      'workspace-search'
    )) {
      if (visits++ >= WORKSPACE_VIEW_MAX_NAVIGATION_VISITS) {
        budget.truncated = true;
        break;
      }
      // The search entries come first; the first other destination ends the offer.
      if (command.navigation?.definition.capability !== 'workspace-search')
        break;
      if (
        sameScope(command.navigation.scope, scope) &&
        command.navigation.definition.available()
      )
        commands.set(command.id, command);
    }
    const actions: WorkspaceNavigationDescriptor[] = [];
    for (const command of commands.values()) {
      // Check immutable fields before encoding or traversing their metadata.
      const definition = command.navigation!.definition;
      const rawOversized =
        !budget.withinBinding(command.id) ||
        !budget.withinBinding(command.navigation!.ownerId) ||
        !budget.withinBinding(definition.label);
      const descriptor = rawOversized
        ? null
        : this.descriptor(command, commands, budget);
      const result = descriptor ? budget.admit(descriptor) : 'oversized';
      if (result !== 'accepted') {
        if (definition.capability === 'workspace-search')
          return { ok: false, reason: result };
        budget.truncated = true;
        break;
      }
      actions.push(descriptor!);
    }
    return { ok: true, actions, truncated: budget.truncated };
  }
  list(scope: WorkspaceViewScope): readonly WorkspaceNavigationDescriptor[] {
    const commands = this.commands(scope);
    const byId = new Map(commands.map(command => [command.id, command]));
    return commands.map(command => this.descriptor(command, byId));
  }
  search(
    scope: WorkspaceViewScope,
    query: string,
    limit: number
  ): readonly WorkspaceNavigationDescriptor[] {
    if (!Number.isInteger(limit) || limit <= 0) return [];
    const commands = this.commands(scope);
    const byId = new Map(commands.map(command => [command.id, command]));
    return matchCommands(commands, query)
      .slice(0, Math.min(limit, 12))
      .map(({ item }) => this.descriptor(item, byId));
  }
  /** Project only already matched native CMDK entries; never run another matcher. */
  describeActions(
    scope: WorkspaceViewScope,
    ids: readonly string[]
  ): readonly WorkspaceNavigationDescriptor[] {
    if (
      this.disposed ||
      !sameScope(scope, this.activeScope()) ||
      ids.length > 12
    )
      return [];
    const budget = new WorkspaceNavigationContextBudget();
    const result: WorkspaceNavigationDescriptor[] = [];
    for (const id of ids) {
      if (!budget.withinBinding(id)) break;
      const command = AffineCommandRegistry.commands.get(id);
      if (
        !command?.navigation ||
        !sameScope(command.navigation.scope, scope) ||
        !command.navigation.definition.available()
      )
        continue;
      const definition = command.navigation.definition;
      if (
        !budget.withinBinding(definition.label) ||
        !budget.withinBinding(command.navigation.ownerId)
      )
        break;
      const descriptor = this.descriptor(
        command,
        AffineCommandRegistry.commands,
        budget
      );
      if (budget.admit(descriptor) !== 'accepted') break;
      result.push(descriptor);
    }
    return result;
  }
  bind(
    scope: WorkspaceViewScope,
    ownerId: string,
    definitionId: string,
    root: HTMLElement,
    element: HTMLElement,
    geometryElement = element,
    withClickArgs?: (args: NavigationArgs, click: () => void) => void
  ): () => void {
    if (this.disposed || scope.workspaceId !== this.activeScope().workspaceId)
      throw new NavigationActivationError('blocked');
    const key = navigationActionId(scope, ownerId, definitionId),
      binding = {
        root,
        element,
        geometryElement,
        definitionId,
        scope: { ...scope },
        acceptsArgs: !!withClickArgs,
        withClickArgs:
          withClickArgs ??
          ((args: NavigationArgs, click: () => void) => {
            if (Object.keys(args).length)
              throw new NavigationActivationError('blocked');
            click();
          }),
      };
    const bindings = this.bindings.get(key) ?? new Set<typeof binding>();
    this.bindings.set(key, bindings);
    bindings.add(binding);
    return () => {
      bindings.delete(binding);
      if (bindings.size === 0 && this.bindings.get(key) === bindings)
        this.bindings.delete(key);
    };
  }
  /**
   * Navigation intents name stable action ids. When the view revision moved
   * only because sources re-registered or re-emitted, the intent is anchored
   * on the live revision while the same view is active and the action is still
   * offered. Drift during execution stays guarded by the executor.
   */
  rebase(request: WorkspaceNavigationRequest): WorkspaceNavigationRequest {
    const scope = { workspaceId: request.workspaceId, viewId: request.viewId };
    const expected = this.context.snapshot(scope, request.revision);
    if (
      expected.ok ||
      expected.reason !== 'stale' ||
      !this.context.wasIssued(scope, request.revision)
    )
      return request;
    const live = this.context.snapshot(scope);
    if (!live.ok || !this.resolveParent(scope, request.actionId).ok)
      return request;
    return { ...request, revision: live.snapshot.revision };
  }
  resolve(request: WorkspaceNavigationRequest): UiNavigationResolveResult {
    const scope = { workspaceId: request.workspaceId, viewId: request.viewId };
    const current = this.context.snapshot(scope, request.revision);
    if (!current.ok)
      return {
        ok: false,
        reason: current.reason === 'stale' ? 'stale' : 'blocked',
      };
    const result = this.resolveParent(scope, request.actionId);
    if (!result.ok) return result;
    if (!validateNavigationArgs(result.definition.argsSchema, request.args))
      return { ok: false, reason: 'blocked' };
    const binding = result.binding as
      | (UiNavigationBinding & { acceptsArgs: boolean })
      | null;
    if (binding && !binding.acceptsArgs && Object.keys(request.args).length)
      return { ok: false, reason: 'blocked' };
    return result;
  }
  resolveParent(
    scope: WorkspaceViewScope,
    actionId: string
  ): UiNavigationResolveResult {
    if (this.disposed) return { ok: false, reason: 'stale' };
    const activeScope = this.activeScope();
    if (!sameScope(scope, activeScope))
      return {
        ok: false,
        reason:
          scope.workspaceId === activeScope.workspaceId ? 'stale' : 'blocked',
      };
    const navigation = AffineCommandRegistry.get(actionId)?.navigation;
    if (
      !navigation ||
      !sameScope(navigation.scope, scope) ||
      !navigation.definition.available()
    )
      return { ok: false, reason: 'blocked' };
    // One normal entry can be displayed in both rail and panel. Prefer its
    // visible geometry; retain connected hidden bindings for the reveal owner.
    const bindings = [...(this.bindings.get(actionId) ?? [])]
      .reverse()
      .filter(
        binding =>
          binding.root.isConnected &&
          binding.element.isConnected &&
          binding.geometryElement.isConnected
      )
      .sort(
        (a, b) =>
          Number(isSemanticDomElementVisible(b.geometryElement, scope)) -
          Number(isSemanticDomElementVisible(a.geometryElement, scope))
      );
    return {
      ok: true,
      definition: navigation.definition,
      binding: bindings[0] ?? null,
    };
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    [...this.sourceCleanups.values()].forEach(cleanup => cleanup());
    this.bindings.clear();
  }
}
